// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@bg/App/Components/Notify";
import loadSystemList from "@bg/App/Handlers/CommonHandlers/LoadSystemList";
import addNewGroup from "@bg/App/Handlers/RequestQuote/AddNewGroup";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { GroupItem, GroupList } from "@bg/Overrides/Components/AddToListModal";
import { myListsDetailsOptionsStyles } from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsOptionsStyles";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    productInfo?: ProductInfo;
    creatingNewGroup: boolean;
    groupingModalIsOpen: boolean;
    shouldLoadCurrentCart?: boolean;
    closeModalCallback: () => void;
    callAfterSuccess?: (newGroupName: string, isMoving?: boolean) => void;
    isMovingItem?: boolean;
    cart?: CartModel | QuoteModel;
    isQuote?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const groupListName = state.context.website.properties["groupsListName"];
    return {
        defaultListGroups: (state.xSystemList as any)[groupListName]?.values,
        uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
        addOnsGroupName: (getSettingsCollection(state).customSettings as any).addOnsGroupName as string,
        groupsNamingRegex: (getSettingsCollection(state).customSettings as any).groupsNamingRegex as string,
        groupListName,
    };
};

const mapDispatchToProps = {
    addCartLines: makeHandlerChainAwaitable(addCartLines as any),
    loadSystemList,
    addNewGroup,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = myListsDetailsOptionsStyles;

const CreateNewGroupModal = ({
    loadSystemList,
    defaultListGroups,
    groupingModalIsOpen,
    creatingNewGroup,
    productInfo,
    closeModalCallback,
    callAfterSuccess,
    isMovingItem,
    uncategorizedGroupName,
    addOnsGroupName,
    addCartLines,
    addNewGroup,
    cart,
    isQuote,
    groupListName,
    shouldLoadCurrentCart,
    groupsNamingRegex,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [selectedGroup, setSelectedGroup] = useState("");
    const [currentStep, setCurrentStep] = useState("");
    const [groupName, setGroupName] = useState("");
    const [newGroupError, setNewGroupError] = useState("");
    const [inProgress, setInProgress] = useState(false);
    const [groups, setGroups] = useState<string[]>([]);
    const nameRegex = new RegExp(groupsNamingRegex);

    useEffect(() => {
        if (groupingModalIsOpen && (!defaultListGroups || !defaultListGroups.length)) {
            loadSystemList({
                systemListName: groupListName,
            });
        }
        if (groupingModalIsOpen) {
            setCurrentStep("");
            setGroupName("");
            setNewGroupError("");
            setSelectedGroup("");
            setInProgress(false);
        }
    }, [groupingModalIsOpen]);

    useEffect(() => {
        if (cart && cart.properties?.groupNames) {
            const groups = cart.properties?.groupNames.split(",") || "";
            setGroups(groups);
        }
    }, [cart]);

    const modalCloseHandler = () => {
        closeModalCallback();
    };

    const newGroupInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGroupName(event.target.value);
        if (event.target.value) {
            setNewGroupError("");
        }
    };

    const addNewGroupAndGoBack = () => {
        if (!groupName || !groupName.trim().length) {
            setNewGroupError(translate("Group Name Required"));
            return;
        }
        if (!nameRegex.test(groupName)) {
            setNewGroupError(siteMessage("Custom_Group_Name_Error_Text") as string);
            return;
        }
        if (groups.filter(name => name.toLowerCase().trim() === groupName.toLowerCase().trim()).length > 0) {
            setNewGroupError(translate("Selected group already exists in the Quote"));
            return;
        }

        creatingNewGroup ? updateCart({}, groupName.trim()) : addItemToQuote({}, groupName.trim());
    };

    const handleReturnFromNewGroup = () => {
        setGroupName("");
        setCurrentStep("");
    };

    const handleGroupSelection = (groupName: string) => {
        if (selectedGroup.toLowerCase() === groupName.toLowerCase()) {
            setSelectedGroup("");
        } else {
            setSelectedGroup(groupName);
        }
    };

    if (!cart) {
        return null;
    }

    const updateCart = (e?: any, newGroup?: string) => {
        const group = selectedGroup || newGroup;
        if (groups.indexOf(group!.toLowerCase()) === -1 && groups.length >= (isQuote ? 9 : 8)) {
            toasterContext.addToast({ body: siteMessage("Custom_MaxAllowedGroups"), messageType: "danger" });
            return;
        }
        if (groups.filter(name => name.toLowerCase() === group!.toLowerCase()).length === 0) {
            setInProgress(true);
            addNewGroup({
                cartId: cart.id,
                newBlankGroupName: group || uncategorizedGroupName,
                reloadCart: shouldLoadCurrentCart ?? !isQuote,
                reloadQuote: isQuote,
                onError: e => {
                    onSubmitError(e);
                },
                onComplete: (result: any) => {
                    if (creatingNewGroup && result.successful) {
                        callAfterSuccess?.(group!, isMovingItem);
                        setInProgress(false);
                        modalCloseHandler();
                    }
                    if (result.successful) {
                        toasterContext.addToast({
                            body: translate("Quote Updated"),
                            messageType: "success",
                        });
                        setInProgress(false);
                        // adding an item case with new group
                        if (!creatingNewGroup) {
                            addItemToQuote();
                        }
                    }
                },
            });
        } else {
            toasterContext.addToast({
                body: translate("Selected group already exists in the quote"),
                messageType: "warning",
            });
            setSelectedGroup("");
        }
    };

    const onSubmitError = (errorMessage: any) => {
        toasterContext.addToast({
            body: translate(`Error: ${errorMessage}`),
            messageType: "danger",
        });
    };

    const addItemToQuote = async (e?: any, newGroup?: string) => {
        const group = selectedGroup || newGroup;
        if (group && groups.indexOf(group!.toLowerCase()) === -1 && groups.length >= (isQuote ? 9 : 8)) {
            toasterContext.addToast({ body: siteMessage("Custom_MaxAllowedGroups"), messageType: "danger" });
            return;
        }
        if (productInfo?.productId) {
            setInProgress(true);
            const result = (await addCartLines({
                cartId: cart.id,
                productInfos: [productInfo],
                newGroup: true,
                groupName: group || uncategorizedGroupName,
                loadCurrentCart: !isQuote,
                reloadQuote: isQuote,
                onError: (error: string) => {
                    modalCloseHandler();
                    setInProgress(false);
                    toasterContext.addToast({ body: translate(error), messageType: "danger" });
                },
            })) as CartLineCollectionModel;

            modalCloseHandler();
            setInProgress(false);
            if (result.cartLines?.length) {
                toasterContext.addToast({ body: translate("Item Added"), messageType: "success" });
                callAfterSuccess?.(group || uncategorizedGroupName, isMovingItem);
            }
        }
    };

    const addItemCartWithGroup = () => {
        addItemToQuote();
    };

    const title =
        currentStep === "" ? siteMessage("Custom_List_ChooseGroup") : siteMessage("Custom_AddToList_AddGroup");

    let customListGroupNamesToDispaly;

    if (groups !== undefined && defaultListGroups !== undefined) {
        // groups = list chosen groups.
        const defaultListGroupsNames = defaultListGroups.map((obj: any) => obj.name.toLowerCase());
        const customListGroupNames = groups.filter(
            (name: string) => !defaultListGroupsNames.includes(name.toLowerCase()),
        );
        customListGroupNamesToDispaly = customListGroupNames.filter(
            (name: string) =>
                name.toLowerCase() !== uncategorizedGroupName.toLowerCase() &&
                name.toLowerCase() !== addOnsGroupName.toLowerCase(),
        );
    }

    return (
        <Modal
            className="add-top-padding"
            size={812}
            headline={title}
            isOpen={groupingModalIsOpen}
            handleClose={modalCloseHandler}
        >
            <StyledWrapper {...styles.stepWrapper} className={currentStep === "" ? "in-view" : ""}>
                <StyledWrapper {...styles.groupsWrapper}>
                    {defaultListGroups &&
                        defaultListGroups.length > 0 &&
                        defaultListGroups.map((group: GroupList) => (
                            <Clickable
                                className={selectedGroup.toLowerCase() === group.name.toLowerCase() ? "selected" : ""}
                                key={group.id}
                                {...styles.groupItem}
                                onClick={() => handleGroupSelection(group.name)}
                            >
                                <GroupItem imagePath={group.additionalInfo} groupName={group.description} />
                            </Clickable>
                        ))}
                    {!isMovingItem &&
                        !creatingNewGroup &&
                        customListGroupNamesToDispaly &&
                        customListGroupNamesToDispaly.length > 0 &&
                        customListGroupNamesToDispaly.map((group: any) => (
                            <Clickable
                                className={selectedGroup.toLowerCase() === group.toLowerCase() ? "selected" : ""}
                                key={group}
                                {...styles.groupItem}
                                onClick={() => handleGroupSelection(group)}
                            >
                                <GroupItem imagePath="null" groupName={group} />
                            </Clickable>
                        ))}
                    <StyledWrapper {...styles.groupItem}>
                        <StyledWrapper {...styles.addNewGroupItem}>
                            <Button onClick={() => setCurrentStep("newGroup")} {...styles.addNewButton}>
                                <ButtonIcon src="Plus" size={16} />
                            </Button>
                        </StyledWrapper>
                        <Typography lineHeight="16px" transform="uppercase" color="secondary">
                            {translate("New Blank Group")}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.buttonsWrapper} className="modal-actions">
                    <Button
                        {...styles.addButton}
                        onClick={creatingNewGroup ? updateCart : addItemCartWithGroup}
                        data-test-selector="productAddToQuote_addButton"
                        disabled={inProgress || (creatingNewGroup && !selectedGroup)}
                    >
                        {inProgress ? (
                            <LoadingSpinner color="secondary" size={28} />
                        ) : creatingNewGroup ? (
                            translate("Create Group")
                        ) : (
                            translate("Add Item To Quote")
                        )}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.stepWrapper} className={currentStep === "newGroup" ? "in-view" : ""}>
                <TextField
                    label={translate("Group Name")}
                    value={groupName}
                    error={newGroupError}
                    maxLength={50}
                    onChange={newGroupInputChangeHandler}
                    placeholder={translate("New Group Name")}
                    {...styles.newListInput}
                    data-test-selector="productAddToQuote_newGroupName"
                />
                {groupsNamingRegex && (
                    <StyledWrapper {...styles.notifyStyle}>
                        <Notify messageType="info" body={siteMessage("Custom_GroupName_Limitations_Text")} />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.buttonsWrapper} className="modal-actions">
                    <Button
                        {...styles.addButton}
                        onClick={addNewGroupAndGoBack}
                        data-test-selector="productAddToQuote_saveGroupAndContinue"
                        disabled={inProgress || newGroupError.length > 0}
                    >
                        {translate("Save and Continue")}
                    </Button>
                    <Button {...styles.cancelButton} onClick={handleReturnFromNewGroup}>
                        {translate("Return to Previous Screen")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateNewGroupModal);
